import { useEffect, useState } from "react";

import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Accordion, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { getPreviousVersionObservations } from "@/encounters/components/EncounterLabor/services/labor";

interface PreviousVersionsProps {
  observation: Observation;
  rowComponent: React.ElementType;
  showPreviousVersions: boolean;
}

export default function PreviousVersions({
  observation,
  rowComponent: RowComponent,
  showPreviousVersions,
}: PreviousVersionsProps) {
  const medplum = useMedplum();

  const [previousVersions, setPreviousVersions] = useState<Observation[]>([]);

  useEffect(() => {
    if (!showPreviousVersions) return;
    const fetchPreviousVersions = async () => {
      const versions = await getPreviousVersionObservations(medplum, observation);
      setPreviousVersions(versions);
    };
    fetchPreviousVersions();
  }, [medplum, observation, showPreviousVersions]);

  return (
    <Accordion
      py="sm"
      multiple
      chevronPosition="right"
      data-testid="previous-versions"
      classNames={{
        control: classes.control,
        label: classes.label,
        content: classes.content,
        item: classes.item,
      }}
    >
      <Accordion.Item value="previous-versions" className={classes.accordionPanel}>
        <Accordion.Control>
          <LastUpdated observation={observation} showPreviousVersions={showPreviousVersions} />
        </Accordion.Control>
        <Accordion.Panel>
          {previousVersions.length > 0 ? (
            <Table>
              <Table.Tbody>
                {previousVersions.map((version, idx) => (
                  <RowComponent
                    observation={version}
                    showNumbersOnly={false}
                    showPreviousVersions={false}
                    time={version.effectiveDateTime}
                    key={`${version.id || "no-id"}-${idx}`}
                    isPreviousVersion={version.status === "cancelled"}
                  />
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <TextWrapper fz="sm">No previous versions</TextWrapper>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}
